import type { Session } from '@supabase/supabase-js';

// `amr` (Authentication Methods Reference) reflects how THIS session was authenticated
// (its login event), never what credentials the user currently has linked. Calling
// supabase.auth.updateUser({ password }) on a session established via OTP/magic-link does
// NOT add "password" to that session's amr -- amr for this session is fixed at mint time,
// same as any other JWT claim (issue #881). UserOnboarding.tsx's handlePasswordSubmit sets
// a persisted `password_set` user_metadata flag in the same updateUser call instead, which
// this reads back directly off session.user (not decoded from the JWT), so it survives a
// reload correctly regardless of amr.
function hasPasswordSet(user: Session['user'] | undefined): boolean {
  return user?.user_metadata?.password_set === true;
}

function hasLinkedOAuthIdentity(identities: Session['user']['identities']): boolean {
  return !!identities?.some((identity) => identity.provider !== 'email');
}

/**
 * True if the session's user already has a real login method (password or a
 * linked OAuth identity), derived from Supabase auth state rather than a DB flag.
 */
export function isCredentialLinked(session: Session | null): boolean {
  if (!session) return false;
  return hasPasswordSet(session.user) || hasLinkedOAuthIdentity(session.user?.identities);
}

/** Skip the invite-onboarding "choose a login method" step when a credential is already linked. */
export function getInitialOnboardingStep(session: Session | null): 'profile' | 'choose' {
  return isCredentialLinked(session) ? 'profile' : 'choose';
}
